import axios, { AxiosRequestConfig, AxiosResponse, AxiosInstance } from 'axios';
import { api as apiConfig } from '../../config';
import { ApiError } from './ApiError';

/**
 * Wrapper class for axios client instance which provides generic methods for
 * the api request types used in this application.
 */
export class ApiClient {
  protected client: AxiosInstance;

  public constructor(token: string) {
    this.client = this.createInstance(token, apiConfig.version || '1.0');
  }

  private createInstance(token: string, version: string): AxiosInstance {
    return axios.create({
      baseURL: `${apiConfig.root.toString()}/v${version}`,
      timeout: apiConfig.httpTimeoutDefault,
      headers: {
        Authorization: token.includes('Bearer') ? token : `Bearer ${token}`,
      },
    });
  }

  public createClientForVersion(version: string): AxiosInstance {
    return this.createInstance(this.client.defaults.headers.Authorization as string, version);
  }

  public get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.unwrap(this.client.get(url, config));
  }

  public delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.unwrap(this.client.delete(url, config));
  }

  public post<T, B>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T> {
    return this.unwrap(this.client.post(url, data, config));
  }

  public put<T, B>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T> {
    return this.unwrap(this.client.put(url, data, config));
  }

  /**
   * Response comes back as { data: <result> } we want to return
   * Promise<result>
   * If the response is not parsable json, it could throw.
   */
  private async unwrap<T>(responsePromise: Promise<AxiosResponse<T>>): Promise<T> {
    try {
      const result = await responsePromise;
      return result.data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        throw new ApiError(e);
      } else {
        throw new ApiError(undefined, (e as Error).message);
      }
    }
  }
}
